import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { sitesApi, authApi, ApiError, subscriptionsApi } from '../../api/client';
import type { SiteTemplate, CreateSiteFromTemplateRequest } from '../../schemas/validation';
import type { DashboardState } from './useDashboardState';

interface UseDashboardHandlersProps {
  state: DashboardState;
  logout: () => void;
}

export const useDashboardHandlers = ({ state, logout }: UseDashboardHandlersProps) => {
  const navigate = useNavigate();

  const loadSites = useCallback(async () => {
    try {
      state.setIsLoading(true);
      const sitesData = await sitesApi.list();
      state.setSites(sitesData);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        authApi.logout();
        navigate('/login');
      } else {
        console.error('Failed to load sites:', error);
      }
    } finally {
      state.setIsLoading(false);
    }
  }, [navigate, state]);

  const loadCurrentPlan = useCallback(async () => {
    try {
      const response = await subscriptionsApi.getCurrentSubscription();

      if (response?.subscription) {
        state.setSubscriptionStatus(response.subscription);
      }

      if (response) {
        if (response.plan) {
          state.setCurrentPlan(response.plan);
        } else if (response.usage?.plan) {
          state.setCurrentPlan(response.usage.plan);
        } else if (response.subscription) {
          const planId = response.subscription.planId || response.subscription.planName;
          if (planId) {
            const planIdUpper = planId.toUpperCase();
            if (planIdUpper.includes('STARTER')) {
              state.setCurrentPlan({
                id: 'starter-plan',
                name: 'STARTER',
                displayName: 'Starter',
              });
            } else if (planIdUpper.includes('PROFESSIONAL')) {
              state.setCurrentPlan({
                id: 'professional-plan',
                name: 'PROFESSIONAL',
                displayName: 'Professional',
              });
            } else {
              state.setCurrentPlan({
                id: 'free-plan',
                name: 'FREE',
                displayName: 'Free',
              });
            }
          }
        }
      }
    } catch {
      state.setCurrentPlan({
        id: 'free-plan',
        name: 'FREE',
        displayName: 'Free',
      });
    }
  }, [state]);

  const handleCreateSite = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!state.newSiteName || !state.newSiteName.trim()) return;

      try {
        state.setIsCreating(true);
        state.setCreateError(null);

        if (state.useTemplate && state.selectedTemplate) {
          const data: CreateSiteFromTemplateRequest = {
            templateId: state.selectedTemplate.id,
            name: state.newSiteName?.trim() || '',
          };

          const result = await sitesApi.createFromTemplate(data);
          await loadSites();

          state.setSuccessMessage(
            `Website "${result.name}" created from template "${state.selectedTemplate.name}"!`
          );
          state.setShowSuccessToast(true);
          setTimeout(() => state.setShowSuccessToast(false), 3000);
        } else {
          const newSite = await sitesApi.create({
            name: state.newSiteName?.trim() || '',
            theme: state.selectedTheme,
          });
          state.setSites(prev => [...prev, newSite]);
        }

        state.setNewSiteName('');
        state.setShowCreateForm(false);
        state.setUseTemplate(false);
        state.setSelectedTemplate(null);
        state.setCreateError(null);
      } catch (error: any) {
        console.error('Failed to create site:', error);

        let errorMessage = 'Failed to create website.';

        if (error.message && error.message.toLowerCase().includes('maximum number of sites')) {
          errorMessage = error.message;
        } else if (state.useTemplate && state.selectedTemplate) {
          if (error.status === 404 || error.status === 500 || !error.status) {
            errorMessage = `Site templates feature is not yet available on the backend. The template "${state.selectedTemplate.name}" is for demo purposes only. Please create a website from scratch for now.`;
          } else {
            errorMessage = `Failed to create website from template "${state.selectedTemplate.name}".`;
          }
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        state.setCreateError(errorMessage);
      } finally {
        state.setIsCreating(false);
      }
    },
    [state, loadSites]
  );

  const handleTemplateSelect = useCallback(
    (template: SiteTemplate) => {
      state.setSelectedTemplate(template);
      state.setNewSiteName(template.siteData?.name || template.name || '');
      state.setUseTemplate(true);
    },
    [state]
  );

  const resetCreateForm = useCallback(() => {
    state.setShowCreateForm(false);
    state.setNewSiteName('');
    state.setSelectedTheme('standard');
    state.setUseTemplate(false);
    state.setSelectedTemplate(null);
    state.setCreateError(null);
  }, [state]);

  const handleDeleteSite = useCallback(
    (site: any) => {
      state.setSiteToDelete(site);
      state.setDeleteError(null);
      state.setShowDeleteModal(true);
    },
    [state]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!state.siteToDelete) return;

    try {
      state.setIsDeleting(true);
      state.setDeleteError(null);

      await sitesApi.delete(state.siteToDelete.id);

      state.setSites(prev => prev.filter(site => site.id !== state.siteToDelete!.id));
      state.setShowDeleteModal(false);
      state.setSiteToDelete(null);

      loadCurrentPlan();

      state.setSuccessMessage(`Website "${state.siteToDelete.name}" deleted successfully.`);
      state.setShowSuccessToast(true);
      setTimeout(() => state.setShowSuccessToast(false), 3000);
    } catch (error: any) {
      console.error('Failed to delete site:', error);

      let errorMessage = 'Failed to delete website.';

      if (error?.status === 500) {
        errorMessage =
          'Server error occurred while deleting the website. Please try again or contact support if the problem persists.';
      } else if (error?.status === 404) {
        errorMessage = 'Website not found. It may have already been deleted.';
      } else if (error?.status === 403) {
        errorMessage = 'You do not have permission to delete this website.';
      } else if (error?.message) {
        errorMessage = `Failed to delete website: ${error.message}`;
      }

      state.setDeleteError(errorMessage);
    } finally {
      state.setIsDeleting(false);
    }
  }, [state, loadCurrentPlan]);

  const handleDeleteCancel = useCallback(() => {
    if (!state.isDeleting) {
      state.setShowDeleteModal(false);
      state.setSiteToDelete(null);
      state.setDeleteError(null);
    }
  }, [state]);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  return {
    loadSites,
    loadCurrentPlan,
    handleCreateSite,
    handleTemplateSelect,
    resetCreateForm,
    handleDeleteSite,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleLogout,
  };
};
